import { pmsRoomLineUnits, type NightPrice } from '@lemontaigu/pms-core';
import { fmtEuros } from './logic.js';

// A room night's price as the booking form's « €/nuit » and the planning pane's
// « Prix / nuit » word it. Both read the SAME rule (`pmsRoomLineUnits`) so the two
// cannot disagree on what « 75,00 » means.
//
// A room line is normally one room. A group Vega prices PER PERSON carries the
// room's heads (« DEMI PENSION GROUPE » 2 × 75 €), read « 75,00 /pers. »; a group
// line Vega could not spread over its rooms carries several rooms on one
// logement, read « 110,00 ×5 ». The price shown is always the UNIT — what the
// desk types back in « Prix… » — and the total is unit × quantity.

export interface RoomLineMark {
  /** What follows the price: '' for one room, « /pers. », « ×5 ». */
  suffix: string;
  /** The breakdown for a tooltip; null for one room. */
  title: string | null;
}

export function roomLineMark(unitCents: number, quantity: number | undefined, heads: number): RoomLineMark {
  const u = pmsRoomLineUnits(quantity, heads);
  if (u.kind === 'perPerson') {
    return {
      suffix: ' /pers.',
      title: `${fmtEuros(unitCents)} × ${u.persons} personnes : tarif de groupe par personne (${fmtEuros(unitCents * u.persons)} la nuit pour le logement)`,
    };
  }
  if (u.kind === 'rooms') {
    return {
      suffix: ` ×${u.rooms}`,
      title: `${u.rooms} chambres à ${fmtEuros(unitCents)} facturées par Vega sur ce seul logement : sa facturation du groupe et sa répartition des chambres ne concordent pas (${fmtEuros(unitCents * u.rooms)} la nuit)`,
    };
  }
  return { suffix: '', title: null };
}

/** The mark of a room's nights: the first night's, and whether the nights differ
 *  in price or in quantity (the « * » the cell already shows for prices). */
export function roomNightsMark(nights: readonly NightPrice[], heads: number): RoomLineMark & { varies: boolean } {
  const first = nights[0];
  if (first === undefined) return { suffix: '', title: null, varies: false };
  const varies = nights.some((n) => n.priceCents !== first.priceCents || (n.quantity ?? 1) !== (first.quantity ?? 1));
  return { ...roomLineMark(first.priceCents, first.quantity, heads), varies };
}
